import {
  LBBadge,
  LBButton,
  LBReplaceSwitchIcon,
  LBScrollArea,
  LBSheet,
  LBSheetContent,
  LBSheetHeader,
  LBSheetTitle,
  LBSkeleton,
  LBSwitch,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import { RelativeDateTooltip } from '../../utils/dateHelpers'
import _ from 'lodash'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { type Insight, type InsightGroup } from '../../apis/organizations'
import InsightsTriggerButton from '../../custom-components/InsightsTriggerButton'
import { trackAction } from '../../lib/datadogRum'
import useInsightsPreferences from '../../hooks/useInsightsPreferences'
import {
  useOrganizationBuyers,
  useOrganizationHasInsights,
  useOrganizationInsights,
  useRefreshOrganizationInsights
} from '../../hooks/queries/useOrganizations'
import {
  computeInsightsFingerprint,
  insightsDismissalStorage
} from '../../utils/insightsDismissalStorage'
import InsightCard from './InsightCard'

const GROUP_LABELS: Record<InsightGroup, string> = {
  call_to_action: 'Call to Action',
  did_you_know: 'Did You Know'
}

const GROUP_ORDER: InsightGroup[] = ['call_to_action', 'did_you_know']

interface OrgInsightsDrawerProps {
  orgId: string | number | undefined
  orgName?: string
  open?: boolean
  onOpenChange?: (next: boolean) => void
  hideTrigger?: boolean
}

const OrgInsightsDrawer = ({
  orgId,
  orgName,
  open: controlledOpen,
  onOpenChange,
  hideTrigger
}: OrgInsightsDrawerProps) => {
  const isControlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next)
    } else {
      setInternalOpen(next)
    }
  }
  const autoOpenAttemptedRef = useRef(false)
  const { prefs } = useInsightsPreferences()
  const gatedOrgId = prefs.enabled ? orgId : undefined
  const { data, isLoading, isError } = useOrganizationInsights(gatedOrgId)
  const refreshMutation = useRefreshOrganizationInsights(gatedOrgId)
  const { data: buyersData } = useOrganizationBuyers(gatedOrgId, open)
  // Lightweight count for the trigger badge so we don't fetch the full buyers
  // list until the user actually opens the drawer.
  const { data: hasInsightsData } = useOrganizationHasInsights(
    !hideTrigger ? gatedOrgId : undefined
  )

  const items: Insight[] = useMemo(() => data?.items ?? [], [data])
  const count = items.length
  const buyers = useMemo(() => buyersData?.items ?? [], [buyersData])
  const triggerBuyerCount = hasInsightsData?.buyer_count ?? 0

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const itemIds = useMemo(() => items.map(i => i.id), [items])
  const allExpanded = count > 0 && itemIds.every(id => expandedIds.has(id))
  const toggleId = (id: string) =>
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  const handleExpandAllChange = (next: boolean) => {
    setExpandedIds(next ? new Set(itemIds) : new Set())
  }

  useEffect(() => {
    setExpandedIds(new Set())
  }, [orgId])

  useEffect(() => {
    setExpandedIds(prev => {
      const present = new Set(itemIds)
      const pruned = new Set([...prev].filter(id => present.has(id)))
      return pruned.size === prev.size ? prev : pruned
    })
  }, [itemIds])

  const fingerprint = useMemo(
    () => computeInsightsFingerprint(items.map(i => i.id)),
    [items]
  )

  const [storedFingerprint, setStoredFingerprint] = useState(() =>
    orgId !== undefined
      ? insightsDismissalStorage.get(orgId)?.fingerprint
      : undefined
  )

  // Re-sync stored fingerprint whenever orgId changes so the drawer doesn't
  // reuse a previous org's dismissal state.
  useEffect(() => {
    setStoredFingerprint(
      orgId !== undefined
        ? insightsDismissalStorage.get(orgId)?.fingerprint
        : undefined
    )
    autoOpenAttemptedRef.current = false
    if (!isControlled) {
      setInternalOpen(false)
    }
  }, [orgId, isControlled])

  // Reset stale open state when insights are globally disabled, so re-enabling
  // doesn't pop the previously-open drawer back into view.
  useEffect(() => {
    if (!prefs.enabled && !isControlled) {
      setInternalOpen(false)
      autoOpenAttemptedRef.current = false
    }
  }, [prefs.enabled, isControlled])

  const dismissedForCurrentSet =
    storedFingerprint !== undefined && storedFingerprint === fingerprint

  useEffect(() => {
    if (isControlled) {
      return
    }
    if (autoOpenAttemptedRef.current) {
      return
    }
    if (isLoading || isError || count === 0 || orgId === undefined) {
      return
    }
    autoOpenAttemptedRef.current = true
    if (!dismissedForCurrentSet && prefs.autoOpen) {
      setInternalOpen(true)
    }
  }, [
    count,
    isLoading,
    isError,
    orgId,
    dismissedForCurrentSet,
    isControlled,
    prefs.autoOpen
  ])

  const handleRefresh = () => {
    trackAction('insights_refreshed', { orgId, orgName })
    refreshMutation.mutate()
  }

  const handleDismissChange = (next: boolean) => {
    if (orgId === undefined) {
      return
    }
    trackAction('insights_dismissed_for_org', { orgId, dismissed: next })
    if (next) {
      insightsDismissalStorage.set(orgId, fingerprint)
      setStoredFingerprint(fingerprint)
    } else {
      insightsDismissalStorage.clear(orgId)
      setStoredFingerprint(undefined)
    }
  }

  const { grouped, orderedGroups } = useMemo(() => {
    const grouped = _.groupBy(
      items,
      (i: Insight) => i.group ?? 'call_to_action'
    )
    const orderedGroups = _.sortBy(Object.keys(grouped), g => {
      const idx = GROUP_ORDER.indexOf(g as InsightGroup)
      return idx === -1 ? Number.POSITIVE_INFINITY : idx
    }) as InsightGroup[]
    return { grouped, orderedGroups }
  }, [items])

  if (!prefs.enabled) {
    return null
  }
  // Keep the open drawer mounted even if a refresh just cleared the last
  // insight, so it shows the empty state instead of vanishing abruptly.
  if (count === 0 && !isControlled && !open) {
    return null
  }

  const skeleton = (
    <div className='space-y-5'>
      <div className='space-y-3'>
        <LBSkeleton className='h-3 w-24' />
        {[0, 1].map(i => (
          <div key={i} className='rounded-md border p-4 space-y-3'>
            <LBSkeleton className='h-4 w-3/4' />
            <LBSkeleton className='h-3 w-1/2' />
          </div>
        ))}
      </div>
    </div>
  )

  let body: React.ReactNode
  // Show the skeleton during a manual refresh too, so the user sees the data
  // actually reload instead of a silent background fetch.
  if (isLoading || refreshMutation.isPending) {
    body = skeleton
  } else if (isError) {
    body = (
      <div className='rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
        Failed to load insights for this organization.
      </div>
    )
  } else if (count === 0) {
    body = (
      <div className='text-sm text-muted-foreground'>
        No insights available for this organization.
      </div>
    )
  } else {
    body = (
      <div className='space-y-5'>
        {orderedGroups.map(group => (
          <div key={group} className='space-y-3'>
            <div className='flex items-center gap-2'>
              <h3 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                {GROUP_LABELS[group] ?? _.startCase(group)}
              </h3>
              {group === 'call_to_action' && buyers.length > 0 && (
                <LBBadge variant='outline' className='text-xs'>
                  {buyers.length === 1
                    ? '1 buyer identified'
                    : `${buyers.length} buyers identified`}
                </LBBadge>
              )}
            </div>
            {grouped[group].map(insight => (
              <InsightCard
                key={insight.id}
                insight={insight}
                expanded={expandedIds.has(insight.id)}
                onToggle={() => {
                  trackAction('insight_details_toggled', {
                    insightId: insight.id,
                    category: insight.category,
                    expanded: !expandedIds.has(insight.id),
                    orgId,
                    orgName
                  })
                  toggleId(insight.id)
                }}
                buyers={buyers}
              />
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      {!hideTrigger && (
        <InsightsTriggerButton
          count={count}
          buyerCount={triggerBuyerCount}
          onClick={() => {
            trackAction('insights_opened', {
              orgId,
              orgName,
              insightsCount: count,
              buyerCount: triggerBuyerCount
            })
            setOpen(true)
          }}
        />
      )}

      <LBSheet open={open} onOpenChange={setOpen}>
        <LBSheetContent
          side='right'
          className='w-full sm:w-[560px] sm:max-w-[560px] p-0 flex flex-col'
        >
          <LBSheetHeader className='px-6 pt-6 pb-4 border-b'>
            <LBSheetTitle className='text-base'>
              Insights
              {orgName && (
                <span className='font-normal text-muted-foreground'>
                  {' · '}
                  {orgName}
                </span>
              )}
              {orgId !== undefined && (
                <span className='ml-2 text-xs font-normal text-muted-foreground/80'>
                  #{orgId}
                </span>
              )}
            </LBSheetTitle>
          </LBSheetHeader>
          <LBScrollArea className='flex-1'>
            <div className='px-6 py-4 space-y-4'>
              {orgId !== undefined && (
                <div className='flex items-center justify-end gap-1.5 text-xs text-muted-foreground'>
                  {refreshMutation.isError && (
                    <span className='text-destructive'>Refresh failed</span>
                  )}
                  <RelativeDateTooltip
                    dateStr={data?.generated_at}
                    prefix='Updated '
                  />
                  <LBTooltip>
                    <LBTooltipTrigger asChild>
                      <LBButton
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 cursor-pointer'
                        disabled={refreshMutation.isPending || isLoading}
                        onClick={handleRefresh}
                        aria-label='Refresh insights'
                      >
                        <LBReplaceSwitchIcon className='h-4 w-4' />
                      </LBButton>
                    </LBTooltipTrigger>
                    <LBTooltipContent>
                      Insights are cached for up to 1 day
                    </LBTooltipContent>
                  </LBTooltip>
                </div>
              )}
              {count > 0 && orgId !== undefined && (
                <div className='flex flex-wrap items-center justify-between gap-x-2 gap-y-3'>
                  <div className='flex items-center gap-2'>
                    <LBSwitch
                      checked={allExpanded}
                      onCheckedChange={handleExpandAllChange}
                      id='insights-expand-all-switch'
                    />
                    <label
                      htmlFor='insights-expand-all-switch'
                      className='text-xs text-muted-foreground cursor-pointer select-none'
                    >
                      {allExpanded ? 'Collapse all' : 'Expand all'}
                    </label>
                  </div>
                  <div className='flex items-center gap-2'>
                    <label
                      htmlFor='insights-dismiss-switch'
                      className='text-xs text-muted-foreground cursor-pointer select-none'
                    >
                      Don't auto-open again for this organization
                    </label>
                    <LBSwitch
                      checked={dismissedForCurrentSet}
                      onCheckedChange={handleDismissChange}
                      id='insights-dismiss-switch'
                    />
                  </div>
                </div>
              )}
              {body}
            </div>
          </LBScrollArea>
        </LBSheetContent>
      </LBSheet>
    </>
  )
}

export default OrgInsightsDrawer
